import React, { useEffect, useState } from 'react';
import { auth } from '../utils/firebase';
import { updatePassword, reauthenticateWithCredential, EmailAuthProvider, sendPasswordResetEmail } from 'firebase/auth';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../components/auth/AuthContext';
import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../utils/firebase';
import PasswordModal from '../components/forms/PasswordModal';
import { collectionPath } from '../utils/firestoreCollections';
import { GamificationProgressPanel } from '../components/gamification/GamificationProgressPanel';
import ReportSubscriptionsPanel from '../components/common/ReportSubscriptionsPanel';
// import CalendarIntegrationsPanel from '../components/common/CalendarIntegrationsPanel'; // HIDDEN until server endpoints deployed

const TIME_ZONES = [
  'UTC',
  'Europe/London',
  'Europe/Berlin',
  'Europe/Paris',
  'Europe/Madrid',
  'Europe/Moscow',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Anchorage',
  'America/Phoenix',
  'America/Sao_Paulo',
  'America/Argentina/Buenos_Aires',
  'Asia/Kolkata',
  'Asia/Shanghai',
  'Asia/Tokyo',
  'Asia/Singapore',
  'Asia/Dubai',
  'Australia/Sydney',
  'Pacific/Auckland',
  'Africa/Johannesburg'
];

// compute UTC offset (minutes) for a timezone using Intl
const getUTCOffsetMinutes = (tz, date = new Date()) => {
  try {
    const dtf = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
    const parts = dtf.formatToParts(date);
    const map = {};
    parts.forEach(p => { if (p.type !== 'literal') map[p.type] = p.value; });
    const y = Number(map.year), mo = Number(map.month), d = Number(map.day);
    const h = Number(map.hour), mi = Number(map.minute), s = Number(map.second);
    const tzLocalAsUtc = Date.UTC(y, mo - 1, d, h, mi, s);
    return Math.round((tzLocalAsUtc - date.getTime()) / 60000);
  } catch (err) {
    return 0;
  }
};

const formatUTCOffset = (minutes) => {
  const sign = minutes <= 0 ? '-' : '+';
  const abs = Math.abs(minutes);
  const hh = String(Math.floor(abs / 60)).padStart(2, '0');
  const mm = String(abs % 60).padStart(2, '0');
  return `UTC${sign}${hh}:${mm}`;
};

// Tab definitions
const TABS = [
  { id: 'general', label: 'General', icon: '👤' },
  { id: 'security', label: 'Security', icon: '🔒' },
  { id: 'subscriptions', label: 'Subscriptions', icon: '📧' },
  { id: 'achievements', label: 'Achievements', icon: '🏆' }
  // { id: 'integrations', label: 'Integrations', icon: '🔗' } // HIDDEN until server endpoints deployed
];

const UserProfilePage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { currentUser, setCurrentUser } = useAuth();
  const uid = currentUser?.uid;

  // Initialize active tab from URL parameter or default to 'general'
  const initialTab = searchParams.get('tab') || 'general';
  const [activeTab, setActiveTab] = useState(
    TABS.some(t => t.id === initialTab) ? initialTab : 'general'
  );
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    displayName: '',
    email: currentUser?.email || '',
    phone: '',
    role: '',
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone || '',
    dateFormat: 'MM/DD/YYYY',
    emailNotifications: true,
    notificationPreferences: {
      taskDigestFrequency: 'none'
    }
  });

  const [twoFA, setTwoFA] = useState(false);

  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [pw1, setPw1] = useState('');
  const [pw2, setPw2] = useState('');
  const [currentPw, setCurrentPw] = useState('');
  const [pwError, setPwError] = useState('');
  const [pwLoading, setPwLoading] = useState(false);
  const [reauthEmail, setReauthEmail] = useState('');
  const [reauthPw, setReauthPw] = useState('');
  const [reauthNeeded, setReauthNeeded] = useState(false);

  useEffect(() => {
    if (!uid) {
      setLoading(false);
      return;
    }

    (async () => {
      setLoading(true);
      try {
        const userDoc = await getDoc(doc(db, collectionPath('users'), uid));
        if (userDoc.exists()) {
          const data = userDoc.data();
          setForm(f => ({ ...f, ...data }));
          if (typeof data.twoFA === 'boolean') setTwoFA(data.twoFA);
        }
      } catch (err) {
        console.warn('Failed to load profile (Firestore)', err);
      } finally {
        setLoading(false);
      }
    })();
  }, [uid]);

  const handleChange = (key, value) => setForm(f => ({ ...f, [key]: value }));

  const handleSave = async () => {
    if (!uid) return alert('Not signed in');
    try {
      setSaving(true);
      const ref = doc(db, collectionPath('users'), uid);
      await updateDoc(ref, { ...form, twoFA, updatedAt: serverTimestamp() });
      setCurrentUser(prev => ({ ...prev, ...form, twoFA }));
      alert('Saved');
    } catch (err) {
      console.error('save failed', err);
      alert('Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = () => {
    setShowPasswordModal(true);
    setPw1('');
    setPw2('');
    setCurrentPw('');
    setPwError('');
    setReauthNeeded(false);
    setReauthEmail(form.email || '');
    setReauthPw('');
  };

  const doPasswordChange = async () => {
    setPwError('');
    if (!currentPw) {
      setPwError('Please enter your current password.');
      return;
    }
    if (!pw1 || !pw2) {
      setPwError('Please enter and confirm your new password.');
      return;
    }
    if (pw1 !== pw2) {
      setPwError('Passwords do not match.');
      return;
    }
    if (pw1.length < 6) {
      setPwError('Password must be at least 6 characters.');
      return;
    }
    setPwLoading(true);
    try {
      const credential = EmailAuthProvider.credential(form.email, currentPw);
      await reauthenticateWithCredential(auth.currentUser, credential);

      if (currentPw === pw1) {
        setPwError('New password must be different from your current password.');
        setPwLoading(false);
        return;
      }

      await updatePassword(auth.currentUser, pw1);
      setShowPasswordModal(false);
      alert('Password updated!');
    } catch (err) {
      if (err.code === 'auth/requires-recent-login') {
        setReauthNeeded(true);
        setPwError('Please re-authenticate to change your password.');
      } else if (err.code === 'auth/wrong-password') {
        setPwError('Current password is incorrect.');
      } else {
        setPwError(err.message || 'Failed to update password.');
      }
    } finally {
      setPwLoading(false);
    }
  };

  const doReauth = async () => {
    setPwError('');
    setPwLoading(true);
    try {
      const credential = EmailAuthProvider.credential(reauthEmail, reauthPw);
      await reauthenticateWithCredential(auth.currentUser, credential);
      setReauthNeeded(false);
      setPwError('');
      await doPasswordChange();
    } catch (err) {
      setPwError('Re-authentication failed: ' + (err.message || ''));
    } finally {
      setPwLoading(false);
    }
  };

  const sendResetEmail = async () => {
    try {
      await sendPasswordResetEmail(auth, form.email);
      alert('Password reset email sent! Check your inbox.');
      setShowPasswordModal(false);
    } catch (err) {
      setPwError(err.message || 'Failed to send reset email.');
    }
  };

  if (loading) return <div className="p-6">Loading profile...</div>;

  return (
    <div>
      <main className="bg-gray-50 p-4 md:p-6 min-h-screen">
        <button className="mb-4 text-indigo-600 hover:underline" onClick={() => navigate(-1)}>
          &larr; Back
        </button>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 max-w-4xl">
          {/* Profile Header */}
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="h-16 w-16 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 text-white flex items-center justify-center text-2xl font-semibold shadow-md">
                  {(form.firstName || form.lastName)
                    ? `${(form.firstName || '').slice(0,1).toUpperCase()}${(form.lastName || '').slice(0,1).toUpperCase()}`
                    : (form.displayName || form.email || '?').split(/\s+/).slice(0,2).map(n => n[0]?.toUpperCase()).join('')}
                </div>
                <div>
                  <div className="text-xl font-bold text-gray-900">
                    {form.displayName || `${form.firstName} ${form.lastName}`.trim() || form.email}
                  </div>
                  <div className="text-sm text-gray-500">{form.email}</div>
                  {form.role && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-800 mt-1">
                      {form.role}
                    </span>
                  )}
                </div>
              </div>

              {activeTab === 'general' && (
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              )}
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="flex border-b border-gray-200">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-6 py-3 text-sm font-medium transition-colors border-b-2 ${
                  activeTab === tab.id
                    ? 'text-indigo-600 border-indigo-500 bg-indigo-50/50'
                    : 'text-gray-500 border-transparent hover:text-gray-700 hover:bg-gray-50'
                }`}
              >
                <span>{tab.icon}</span>
                <span>{tab.label}</span>
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {/* General Tab */}
            {activeTab === 'general' && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Left Column */}
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                      <input
                        value={form.firstName}
                        onChange={e => handleChange('firstName', e.target.value)}
                        className="w-full border border-gray-300 p-2.5 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                      <input
                        value={form.lastName}
                        onChange={e => handleChange('lastName', e.target.value)}
                        className="w-full border border-gray-300 p-2.5 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                      <input
                        value={form.phone}
                        onChange={e => handleChange('phone', e.target.value)}
                        className="w-full border border-gray-300 p-2.5 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                        placeholder="+1 (555) 123-4567"
                      />
                    </div>
                  </div>

                  {/* Right Column */}
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                      <input
                        value={form.email}
                        onChange={e => handleChange('email', e.target.value)}
                        className="w-full border border-gray-300 p-2.5 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Time Zone</label>
                      <select
                        value={form.timeZone || ''}
                        onChange={e => handleChange('timeZone', e.target.value)}
                        className="w-full border border-gray-300 p-2.5 rounded-lg bg-white focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      >
                        <option value="">(Use browser default: {Intl.DateTimeFormat().resolvedOptions().timeZone})</option>
                        {TIME_ZONES.map(tz => {
                          const minutes = getUTCOffsetMinutes(tz);
                          const label = `${tz.replace('_', ' ')} (${formatUTCOffset(minutes)})`;
                          return <option key={tz} value={tz}>{label}</option>;
                        })}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Date Format</label>
                      <select
                        value={form.dateFormat}
                        onChange={e => handleChange('dateFormat', e.target.value)}
                        className="w-full border border-gray-300 p-2.5 rounded-lg bg-white focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      >
                        <option>MM-DD-YYYY</option>
                        <option>MM/DD/YYYY</option>
                        <option>DD/MM/YYYY</option>
                        <option>YYYY-MM-DD</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Security Tab */}
            {activeTab === 'security' && (
              <div className="space-y-6">
                {/* Assigned Role */}
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="text-xl">👤</span>
                    <h3 className="font-semibold text-gray-900">Assigned Role</h3>
                  </div>
                  <div className="ml-8">
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-indigo-100 text-indigo-800">
                      {form.role || 'User'}
                    </span>
                    <p className="text-sm text-gray-500 mt-2">
                      Your role determines what features and data you can access. Contact an administrator to change your role.
                    </p>
                  </div>
                </div>

                {/* Change Password */}
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="text-xl">🔑</span>
                    <h3 className="font-semibold text-gray-900">Change Password</h3>
                  </div>
                  <div className="ml-8">
                    <p className="text-sm text-gray-500 mb-3">
                      Update your password regularly for better security. You'll need your current password to make changes.
                    </p>
                    <button
                      className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm font-medium transition-colors"
                      onClick={handleChangePassword}
                    >
                      Change Password
                    </button>
                  </div>
                </div>

                {/* Two-Factor Authentication (commented out but styled) */}
                {/* 
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="text-xl">🛡️</span>
                    <h3 className="font-semibold text-gray-900">Two-Factor Authentication</h3>
                  </div>
                  <div className="ml-8">
                    <label className="flex items-center space-x-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={twoFA}
                        onChange={e => setTwoFA(e.target.checked)}
                        className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                      />
                      <span className="text-sm text-gray-700">Enable Two-Factor Authentication (2FA)</span>
                    </label>
                    <p className="text-sm text-gray-500 mt-2">
                      {twoFA ? '2FA is enabled. Follow provider steps to finish setup.' : '2FA adds an extra layer of security to your account.'}
                    </p>
                  </div>
                </div>
                */}

                {/* Security Notes */}
                <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
                  <div className="flex items-start gap-3">
                    <span className="text-xl">💡</span>
                    <div>
                      <h4 className="font-medium text-amber-800 mb-1">Security Tips</h4>
                      <ul className="text-sm text-amber-700 space-y-1 list-disc list-inside">
                        <li>Use a unique, strong password that you don't use elsewhere</li>
                        <li>Changing your password may require re-authentication</li>
                        <li>Never share your login credentials with others</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Achievements Tab */}
            {activeTab === 'achievements' && (
              <div>
                <GamificationProgressPanel variant="page" showHeader={true} />
              </div>
            )}

            {/* Subscriptions Tab */}
            {activeTab === 'subscriptions' && (
              <div>
                <ReportSubscriptionsPanel showHeader={true} />
              </div>
            )}

            {/* Integrations Tab - HIDDEN until server endpoints deployed
            {activeTab === 'integrations' && (
              <div>
                <CalendarIntegrationsPanel showHeader={true} />
              </div>
            )}
            */}
          </div>
        </div>
      </main>

      {/* Password Change Modal */}
      <PasswordModal
        show={showPasswordModal}
        onClose={() => setShowPasswordModal(false)}
        reauthNeeded={reauthNeeded}
        pwError={pwError}
        pwLoading={pwLoading}
        currentPw={currentPw}
        setCurrentPw={setCurrentPw}
        pw1={pw1}
        setPw1={setPw1}
        pw2={pw2}
        setPw2={setPw2}
        reauthEmail={reauthEmail}
        setReauthEmail={setReauthEmail}
        reauthPw={reauthPw}
        setReauthPw={setReauthPw}
        doPasswordChange={doPasswordChange}
        doReauth={doReauth}
        sendResetEmail={sendResetEmail}
      />
    </div>
  );
};

export default UserProfilePage;